// Sanitises Lexical's exported HTML so it matches the output format produced
// by Author v1's RTField. Only tags and attributes that v1's RTField emits are
// kept, everything else is unwrapped or dropped.

const ALLOWED_ANCHOR_ATTRIBUTES: ReadonlyArray<string> = [
  "href",
  "title",
  "target",
  "rel",
];
const ALLOWED_PIPE_ATTRIBUTES: ReadonlyArray<string> = [
  "data-piped",
  "data-id",
  "data-type",
];

const HEADING_TAGS: ReadonlySet<string> = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
const escape = (value: string): string =>
  value.replace(/[&<>"]/g, (character) => HTML_ESCAPES[character]);

const attributesString = (
  element: Element,
  allowed: ReadonlyArray<string>,
): string => {
  let result: string = "";
  allowed.forEach((name) => {
    if (element.hasAttribute(name)) {
      result += ` ${name}="${escape(element.getAttribute(name) ?? "")}"`;
    }
  });
  return result;
};

const renderChildren = (parent: Node): string => {
  let result: string = "";
  parent.childNodes.forEach((child) => {
    result += renderNode(child);
  });
  return result;
};

const hasStrongAncestor = (element: Element): boolean =>
  element.parentElement?.closest("strong, b, mark") != null;

const renderNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return escape(node.textContent ?? "");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element: Element = node as Element;
  const tag: string = element.tagName.toLowerCase();

  // Tag normalisation — match v1 RTField's allowed set.
  // v1 only uses <strong> for bold and highlight. Lexical may still emit
  // <b> or legacy <mark>; normalise those to <strong>.
  // Headings other than <h2> are normalised to <h2> (the only heading v1 supports).
  // <ol> is converted to <ul> (ordered lists are not supported by v1).
  // v1's LinkPlugin emits <a href="..." target="_blank" rel="noopener noreferrer">,
  if (tag === "br") {
    return "<br>";
  }

  if (tag === "p" || tag === "li" || tag === "ul") {
    return `<${tag}>${renderChildren(element)}</${tag}>`;
  }

  if (HEADING_TAGS.has(tag)) {
    return `<h2>${renderChildren(element)}</h2>`;
  }

  if (tag === "ol") {
    return `<ul>${renderChildren(element)}</ul>`;
  }

  if (tag === "strong" || tag === "b" || tag === "mark") {
    // Avoid nesting <strong> inside <strong> when Lexical wraps <b><strong>
    // or combines bold + highlight.
    if (hasStrongAncestor(element)) {
      return renderChildren(element);
    }
    return `<strong>${renderChildren(element)}</strong>`;
  }

  if (tag === "a") {
    return `<a${attributesString(element, ALLOWED_ANCHOR_ATTRIBUTES)}>${renderChildren(element)}</a>`;
  }

  if (tag === "span" && element.hasAttribute("data-piped")) {
    return `<span${attributesString(element, ALLOWED_PIPE_ATTRIBUTES)}>${renderChildren(element)}</span>`;
  }

  // Everything else (em, i, u, s, sub, sup, code, div, span without piping,
  // blockquote, etc.) is unwrapped so the text is preserved but the tag is not.
  return renderChildren(element);
};

export const sanitiseHtml = (html: string): string => {
  if (!html) {
    return "";
  }
  const parser: DOMParser = new DOMParser();
  const document: Document = parser.parseFromString(html, "text/html");
  return renderChildren(document.body);
};
